/* eslint react/prop-types: 0 */
import React, { useState } from 'react';
import axios from 'axios';
import {
  PenLine, CheckCircle2, AlertCircle, Loader2, Stamp, X,
} from 'lucide-react';
import SignModal from './SignModal';

/**
 * SignaturePanel — the "Sign Digitally" stage block (party + company-user).
 *
 * Visible only when:
 *   - All parties have approved (execution.all_approved === true)
 *   - Document is NOT marked for physical signing
 *   - Document is not already executed / processing
 *
 * Behaviour:
 *   - If the current principal hasn't signed → "Sign Digitally" primary CTA
 *     opens the SignModal.
 *   - If they've already signed → "✓ You signed on {date}" confirmation.
 *   - Always shows a "Signed: X of Y" progress chip computed from the
 *     execution.signed_count / execution.total_count payload returned by the
 *     detail endpoint.
 *
 * The Execute button itself lives in ExecutionPanel — this panel is purely
 * about getting signatures.
 *
 * Props:
 *   doc, docEndpointBase, authHeaders, onRefresh, testIdPrefix
 */
export default function SignaturePanel({
  doc, docEndpointBase, authHeaders, onRefresh, testIdPrefix = 'vdr',
}) {
  const [showModal, setShowModal] = useState(false);
  const execution = doc?.execution || {};
  const yourApproval = doc?.your_approval || null;

  const allApproved = !!execution.all_approved;
  const signingMode = execution.signing_mode || null;
  const status = execution.status || 'pending';
  const signedCount = execution.signed_count || 0;
  const totalCount = execution.total_count || 0;
  const youSigned = !!(yourApproval?.signature?.consent);

  // Only render the Sign stage in the narrow window: all approved, digital
  // path, pre-execution. Everything else is handled by ExecutionPanel.
  if (!allApproved) return null;
  if (signingMode === 'physical') return null;
  if (status === 'processing' || status === 'executed') return null;
  if (!yourApproval) return null;  // not on the approver list

  const prefillName = yourApproval?.name || '';
  const signEndpoint = `${docEndpointBase}/sign`;

  let signedAtLabel = '—';
  if (youSigned && yourApproval.signature?.signed_at) {
    try {
      signedAtLabel = new Date(yourApproval.signature.signed_at)
        .toLocaleString('en-US', {
          year: 'numeric', month: 'short', day: 'numeric',
          hour: '2-digit', minute: '2-digit',
        });
    } catch { /* ignore */ }
  }

  return (
    <div
      className="mb-5 bg-blue-500/5 border border-blue-400/30 rounded-lg p-4"
      data-testid={`${testIdPrefix}-signature-panel`}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-blue-500/15 border border-blue-400/40 flex items-center justify-center shrink-0">
          <PenLine className="w-5 h-5 text-blue-300" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-sm font-semibold text-blue-100">
              Sign digitally
            </p>
            <span
              className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-200 border border-blue-400/30"
              data-testid={`${testIdPrefix}-signed-progress-chip`}
            >
              Signed: {signedCount} of {totalCount}
            </span>
          </div>

          {youSigned ? (
            <div
              className="mt-2 flex items-start gap-1.5 text-xs text-[#059669]"
              data-testid={`${testIdPrefix}-you-signed-confirmation`}
            >
              <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>
                You signed on{' '}
                <span className="text-white">{signedAtLabel}</span>.
                {signedCount < totalCount
                  ? ` Waiting for ${totalCount - signedCount} more.`
                  : ' All parties have signed — anyone can now execute.'}
              </span>
            </div>
          ) : (
            <>
              <p className="text-xs text-gray-300 mt-1">
                Add your digital signature to move this document toward
                execution. Your typed name will be rendered in cursive and
                appended as a signature page.
              </p>
              <button
                type="button"
                onClick={() => setShowModal(true)}
                className="mt-3 inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-400 text-white font-semibold rounded px-4 py-2 text-sm"
                data-testid={`${testIdPrefix}-sign-btn`}
              >
                <PenLine className="w-4 h-4" /> Sign Digitally
              </button>
            </>
          )}
        </div>
      </div>

      <SignModal
        open={showModal}
        onClose={() => setShowModal(false)}
        onSigned={onRefresh}
        signEndpoint={signEndpoint}
        authHeaders={authHeaders}
        prefillName={prefillName}
        testIdPrefix={testIdPrefix}
      />
    </div>
  );
}


/**
 * AdminMarkPhysicalPanel — super-admin-only control to flip the whole
 * document into PHYSICAL signing mode. Visible only when:
 *   - All parties have approved
 *   - No signing_mode has been chosen yet
 *   - No party has signed digitally yet (server enforces this too)
 *   - Document is not already executing/executed
 *
 * Props: doc, docEndpointBase (admin variant), authHeaders, onRefresh
 */
export function AdminMarkPhysicalPanel({
  doc, docEndpointBase, authHeaders, onRefresh,
}) {
  const [confirming, setConfirming] = useState(false);
  const [posting, setPosting] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  const execution = doc?.execution || {};
  const allApproved = !!execution.all_approved;
  const signingMode = execution.signing_mode || null;
  const status = execution.status || 'pending';
  const signedCount = execution.signed_count || 0;

  if (!allApproved) return null;
  if (status === 'processing' || status === 'executed') return null;

  // Already marked: show a small audit badge.
  if (signingMode === 'physical') {
    const setBy = execution.signing_mode_set_by || {};
    let setAt = '—';
    try {
      if (execution.signing_mode_set_at) {
        setAt = new Date(execution.signing_mode_set_at).toLocaleString('en-US', {
          year: 'numeric', month: 'short', day: 'numeric',
          hour: '2-digit', minute: '2-digit',
        });
      }
    } catch { /* ignore */ }
    return (
      <div
        className="mb-5 bg-amber-500/5 border border-amber-400/30 rounded-lg p-3 text-xs text-amber-200 flex items-start gap-2"
        data-testid="admin-physical-badge"
      >
        <Stamp className="w-4 h-4 shrink-0 mt-0.5" />
        <span>
          Marked for <strong>physical signing</strong> by{' '}
          <span className="text-white">{setBy.name || setBy.email || '—'}</span>{' '}
          on <span className="text-white">{setAt}</span>. Execution will skip
          the digital signature page.
        </span>
      </div>
    );
  }

  if (signedCount > 0) {
    // Surface why the control is no longer available — server-side gate is
    // identical, but UX should not silently hide the option.
    return (
      <div
        className="mb-5 bg-[#111827] border border-gray-800 rounded-lg p-3 text-xs text-gray-400 flex items-start gap-2"
        data-testid="admin-mark-physical-locked"
      >
        <Stamp className="w-4 h-4 shrink-0 mt-0.5 opacity-60" />
        <span>
          Physical-sign mode is no longer available — {signedCount} party
          {signedCount === 1 ? ' has' : ' have'} already signed digitally.
        </span>
      </div>
    );
  }

  const handleMark = async () => {
    setErrMsg(''); setPosting(true);
    try {
      await axios.post(
        `${docEndpointBase}/mark-physical`,
        { confirm: true },
        { headers: authHeaders },
      );
      setConfirming(false);
      await onRefresh?.();
    } catch (e) {
      setErrMsg(
        e?.response?.data?.detail
        || 'Could not mark document for physical signing.',
      );
    } finally {
      setPosting(false);
    }
  };

  return (
    <div
      className="mb-5 bg-[#111827] border border-gray-700 rounded-lg p-3"
      data-testid="admin-mark-physical-panel"
    >
      <div className="flex items-start gap-2">
        <Stamp className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-xs text-gray-200">
            Will this document be signed <strong>physically</strong> (wet ink)
            outside the data room?
          </p>
          <p className="text-[11px] text-gray-500 mt-1">
            Marking it physical skips digital signatures entirely and lets you
            execute right away. This is irreversible.
          </p>
          <button
            type="button"
            onClick={() => setConfirming(true)}
            className="mt-2 inline-flex items-center gap-1.5 text-xs text-amber-200 hover:text-amber-100 border border-amber-400/40 hover:border-amber-300 rounded px-2.5 py-1.5"
            data-testid="admin-mark-physical-btn"
          >
            Mark for physical signing
          </button>
        </div>
      </div>

      {confirming && (
        <div
          className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4"
          data-testid="mark-physical-confirm-dialog"
          onClick={(ev) => { if (ev.target === ev.currentTarget && !posting) setConfirming(false); }}
        >
          <div className="w-full max-w-md bg-[#0b1310] border border-gray-700 rounded-xl shadow-2xl">
            <div className="px-5 py-3 border-b border-gray-800 flex items-center justify-between">
              <p className="text-sm font-semibold text-white">Mark for physical signing?</p>
              <button
                type="button"
                onClick={() => !posting && setConfirming(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-5 py-4 text-xs text-gray-300 space-y-2">
              <p>
                This document will be flagged as <strong>physically signed</strong>.
                The Execute button will be enabled immediately and the
                digital signature step will be hidden for every party.
              </p>
              <p className="text-amber-300">
                This action is irreversible.
              </p>
              {errMsg && (
                <p className="text-red-400 inline-flex items-start gap-1.5">
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {errMsg}
                </p>
              )}
            </div>
            <div className="px-5 py-3 border-t border-gray-800 flex justify-end gap-2 bg-black/30">
              <button
                type="button"
                onClick={() => !posting && setConfirming(false)}
                disabled={posting}
                className="px-3 py-2 text-xs text-gray-300 hover:text-white disabled:opacity-40"
                data-testid="mark-physical-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleMark}
                disabled={posting}
                className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black font-semibold rounded px-4 py-2 text-xs"
                data-testid="mark-physical-confirm"
              >
                {posting && <Loader2 className="w-4 h-4 animate-spin" />}
                Yes, mark physical
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
